package com.downdogapp.client

import com.downdogapp.client.api.RecordSongPlayRequest
import com.downdogapp.client.api.SongSample
import com.downdogapp.client.singleton.Network

object MediaSampleManager {

  fun onManifestFetched() {
    numSamplesPlayed.clear()
    preloadingAudioPlayers.values.forEach { it.first.releasePlayer() }
    preloadingAudioPlayers.clear()
    preloadingVideoPlayers.values.forEach { it.releasePlayer() }
    preloadingVideoPlayers.clear()
    preloadingServerImages.clear()

    for (typeId in SequenceSettings.allSelectorTypeIds) {
      val selectedId = SequenceSettings.getSelectedId(typeId)
      if (selectedId != null) {
        preloadAudioPlayer(typeId, selectedId)
        preloadServerImage(typeId, selectedId)
        preloadVideoPlayer(typeId, selectedId)
      }
    }
  }

  fun onStartingPractice() {
    preloadingAudioPlayers.values.forEach { it.first.releasePlayer() }
    preloadingAudioPlayers.clear()
    preloadingVideoPlayers.values.forEach { it.releasePlayer() }
    preloadingVideoPlayers.clear()
    preloadingServerImages.clear()
  }

  fun onPushedSelector(typeId: Int) {
    for (option in SequenceSettings.getOptions(typeId)) {
      preloadAudioPlayer(typeId, option.id)
      preloadServerImage(typeId, option.id)
      preloadVideoPlayer(typeId, option.id)
    }
  }

  fun onPoppedSelector(typeId: Int) {
    val selectedId = SequenceSettings.getSelectedId(typeId)
    for ((ids, _) in preloadingAudioPlayers.toList()) { // prevent concurrent modification
      if (ids.first == typeId && ids.second != selectedId) {
        preloadingAudioPlayers.remove(ids)?.first?.releasePlayer()
        preloadingVideoPlayers.remove(ids)?.releasePlayer()
        preloadingServerImages.remove(ids)
      }
    }
  }

  fun getImage(typeId: Int, optionId: Int): ServerImage? {
    if (preloadingServerImages[typeId to optionId] == null) {
      if (!preloadServerImage(typeId, optionId)) {
        return null
      }
    }

    return preloadingServerImages[typeId to optionId]
  }

  private fun preloadServerImage(typeId: Int, optionId: Int): Boolean {
    val option =
      SequenceSettings.getOptions(typeId).find { it.id == optionId }
      ?: return false

    val imageUrl = option.imageUrl ?: return false

    preloadingServerImages[typeId to optionId]?.let { return true }

    preloadingServerImages[typeId to optionId] = createServerImage(url = imageUrl)
    return true
  }

  fun getVideoPlayer(typeId: Int, optionId: Int): MediaPlayer? {
    if (preloadingVideoPlayers[typeId to optionId] == null) {
      if (!preloadVideoPlayer(typeId, optionId)) {
        return null
      }
    }

    return preloadingVideoPlayers[typeId to optionId]
  }

  private fun preloadVideoPlayer(typeId: Int, optionId: Int): Boolean {
    val option =
      SequenceSettings.getOptions(typeId).find { it.id == optionId }
      ?: return false

    val videoUrl = option.videoUrl ?: return false

    val player =
      preloadingVideoPlayers[typeId to optionId]
      ?: videoPlayer(loop = true)

    player.setFile(CommonUri(videoUrl))
    preloadingVideoPlayers[typeId to optionId] = player

    return true
  }

  fun getAudioPlayer(typeId: Int, optionId: Int): Pair<MediaPlayer, SongSample?>? {
    if (preloadingAudioPlayers[typeId to optionId] == null) {
      if (!preloadAudioPlayer(typeId, optionId)) {
        return null
      }
    }
    val (player, songSample) = preloadingAudioPlayers.remove(typeId to optionId)!!
    if (songSample != null) {
      Network.post(RecordSongPlayRequest(songId = songSample.id, playlistId = null))
      numSamplesPlayed[typeId to optionId] = (numSamplesPlayed[typeId to optionId] ?: 0) + 1
    }
    preloadAudioPlayer(typeId, optionId)

    return player to songSample
  }

  private fun preloadAudioPlayer(typeId: Int, optionId: Int): Boolean {
    val option =
      SequenceSettings.getOptions(typeId).find { it.id == optionId }
      ?: return false

    val (audioUrl, songSample) =
      option.audioUrl?.let { it to null }
      ?: option.songSamples.getOrNull(numSamplesPlayed[typeId to optionId] ?: 0)?.let { it.url to it }
      ?: return false

    val player =
      preloadingAudioPlayers[typeId to optionId]?.first
      ?: audioPlayer()

    player.setFile(CommonUri(audioUrl))
    preloadingAudioPlayers[typeId to optionId] = player to songSample

    return true
  }
}

private val numSamplesPlayed = mutableMapOf<Pair<Int, Int>, Int>()
private val preloadingAudioPlayers = mutableMapOf<Pair<Int, Int>, Pair<MediaPlayer, SongSample?>>()
private val preloadingVideoPlayers = mutableMapOf<Pair<Int, Int>, MediaPlayer>()
private val preloadingServerImages = mutableMapOf<Pair<Int, Int>, ServerImage>()